import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Plus, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { PetForm } from "@/components/PetForm";
import { PetAvatar } from "@/components/PetAvatar";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TEMPLATES = {
  dog: [["feeding", "Breakfast", ["08:00"]], ["feeding", "Dinner", ["18:00"]], ["walk", "Walk", ["07:30", "19:00"]]],
  cat: [["feeding", "Breakfast", ["08:00"]], ["feeding", "Dinner", ["18:00"]], ["litter", "Scoop litter", ["20:00"]]],
  other: [["feeding", "Food & water", ["08:00"]]],
};

function StepHousehold({ onDone }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/household", { name, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, referral_code: localStorage.getItem("pl_ref") || null });
      localStorage.removeItem("pl_ref");
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <label className="pl-label">Household name</label>
      <input required autoFocus data-testid="household-name-input" className="pl-input" placeholder="e.g. The Garcia Pack" value={name} onChange={(e) => setName(e.target.value)} />
      <button disabled={busy} data-testid="household-create-btn" className="pl-btn-primary w-full">Continue <ArrowRight className="h-4 w-4" /></button>
    </form>
  );
}

function StepPets({ pets, setPets, onNext }) {
  const [adding, setAdding] = useState(pets.length === 0);
  const [saving, setSaving] = useState(false);
  const save = async (f, file) => {
    setSaving(true);
    try {
      let { data } = await api.post("/pets", f);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        data = (await api.post(`/pets/${data.id}/photo`, fd)).data;
      }
      setPets([...pets, data]);
      setAdding(false);
    } catch (err) { toast.error(errMsg(err)); }
    setSaving(false);
  };
  return (
    <div className="space-y-4">
      {pets.map((p) => (
        <div key={p.id} className="pl-card p-3 flex items-center gap-3" data-testid={`onboarding-pet-${p.name}`}>
          <PetAvatar pet={p} size={48} /><div className="flex-1"><p className="font-heading font-semibold">{p.name}</p><p className="text-xs text-slate-500 capitalize">{p.species} {p.breed && `· ${p.breed}`}</p></div>
          <Check className="text-[#10B981]" />
        </div>
      ))}
      {adding ? <div className="pl-card p-5"><PetForm compact onSubmit={save} saving={saving} submitLabel="Add pet" /></div>
        : <button data-testid="onboarding-add-another-pet-btn" onClick={() => setAdding(true)} className="pl-btn-ghost w-full"><Plus className="h-4 w-4" />Add another pet</button>}
      {pets.length > 0 && !adding && <button data-testid="onboarding-pets-next-btn" onClick={onNext} className="pl-btn-primary w-full">Next: routines <ArrowRight className="h-4 w-4" /></button>}
    </div>
  );
}

function StepRoutines({ pets, onFinish }) {
  const all = pets.flatMap((p) => (TEMPLATES[p.species] || TEMPLATES.other).map(([type, title, times], i) => ({ key: `${p.id}-${i}`, pet: p, type, title, times })));
  const [sel, setSel] = useState(() => new Set(all.map((a) => a.key)));
  const [busy, setBusy] = useState(false);
  const finish = async () => {
    setBusy(true);
    try {
      for (const a of all.filter((x) => sel.has(x.key))) {
        await api.post("/schedules", { pet_id: a.pet.id, type: a.type, title: a.title, times: a.times, frequency: "daily" });
      }
      onFinish();
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">Pick starter routines. You can edit times and add meds right after.</p>
      {all.map((a) => (
        <button key={a.key} data-testid={`routine-${a.key}`} onClick={() => { const n = new Set(sel); n.has(a.key) ? n.delete(a.key) : n.add(a.key); setSel(n); }}
          className={`pl-card w-full p-3 flex items-center gap-3 text-left ${sel.has(a.key) ? "border-[#F59E0B] bg-[#FFFBF0]" : ""}`}>
          <PetAvatar pet={a.pet} size={40} />
          <div className="flex-1"><p className="font-semibold">{a.pet.name}: {a.title}</p><p className="text-xs text-slate-500">Daily · {a.times.join(", ")}</p></div>
          <span className={`h-7 w-7 rounded-full grid place-items-center ${sel.has(a.key) ? "bg-[#F59E0B]" : "border-2"}`}>{sel.has(a.key) && <Check className="h-4 w-4" />}</span>
        </button>
      ))}
      <button disabled={busy} data-testid="onboarding-finish-btn" onClick={finish} className="pl-btn-primary w-full">{busy ? "Setting up..." : "Go to today's checklist"}</button>
    </div>
  );
}

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(user?.household_id ? 1 : 0);
  const [pets, setPets] = useState([]);
  const titles = ["Name your household", "Add your pets", "Daily routines"];
  const finish = async () => { await refresh(); nav("/app", { replace: true }); };
  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <div className="max-w-lg mx-auto px-5 py-6">
        <Logo />
        <div className="flex gap-2 mt-8 mb-6">{titles.map((t, i) => <span key={t} className={`h-2 flex-1 rounded-full ${i <= step ? "bg-[#F59E0B]" : "bg-[#EFECE6]"}`} />)}</div>
        <p className="text-sm font-semibold text-slate-500">Step {step + 1} of 3</p>
        <h1 className="font-heading text-4xl font-extrabold mb-6" data-testid="onboarding-step-title">{titles[step]}</h1>
        {step === 0 && <StepHousehold onDone={async () => { await refresh(); setStep(1); }} />}
        {step === 1 && <StepPets pets={pets} setPets={setPets} onNext={() => setStep(2)} />}
        {step === 2 && <StepRoutines pets={pets} onFinish={finish} />}
        {step > 0 && <button data-testid="onboarding-skip-btn" onClick={finish} className="mt-6 text-sm text-slate-500 w-full">Skip for now</button>}
      </div>
    </div>
  );
}
